/**
 * menuVisibilityBus.js
 *
 * A tiny pub-sub for "the admin just changed the Menu Directory".
 *
 * Two problems it solves at once:
 *   1. Same-tab: the admin saves in Menu Directory, then navigates to
 *      /account/notifications (or any page reading the menu-visibility
 *      context) — the notification page's AppShell fetches
 *      /menu-visibility on mount, so a fresh navigation was already
 *      fine. But if the admin uses the sidebar to switch between the
 *      two pages via React Router links, the AppShell BETWEEN the two
 *      pages might not fully unmount (unlikely today but future-safe).
 *      A same-tab CustomEvent broadcast makes the refresh explicit.
 *   2. Cross-tab: the admin has Menu Directory open in one tab and the
 *      notification page open in another. Without a signal, the second
 *      tab keeps serving its stale sessionStorage cache until the
 *      founder reloads. `BroadcastChannel` lets tabs on the same
 *      origin talk to each other; every open tab clears its local
 *      cache and re-fetches when this channel emits.
 *
 * Failure modes: `BroadcastChannel` is not available on very old
 * browsers or in some iframe / sandboxed contexts — we swallow the
 * construction error and fall back to same-tab-only broadcast so the
 * caller never has to think about it.
 */
const CHANNEL_NAME = "cpg-menu-directory";
const WINDOW_EVENT = "cpg:menu-visibility-changed";

// Sessions caches to clear on every refresh so a subsequent
// AppShell mount can't paint from a stale snapshot even before the
// re-fetch resolves.
const SESSION_CACHE_KEYS = ["cpg.menu.layout"];

function clearSessionCaches() {
  try {
    for (const key of SESSION_CACHE_KEYS) window.sessionStorage.removeItem(key);
  } catch { /* private mode / disabled storage — safe to ignore */ }
}

let bc = null;
function getChannel() {
  if (bc !== null) return bc;
  try {
    bc = typeof window !== "undefined" && "BroadcastChannel" in window
      ? new window.BroadcastChannel(CHANNEL_NAME)
      : null;
  } catch {
    bc = null;
  }
  return bc;
}

/** Publish a "menu directory changed" event to every open tab. */
export function broadcastMenuVisibilityChanged() {
  clearSessionCaches();
  try {
    window.dispatchEvent(new CustomEvent(WINDOW_EVENT));
  } catch { /* older browsers without CustomEvent constructor — very unlikely */ }
  const ch = getChannel();
  if (ch) {
    try { ch.postMessage({ type: WINDOW_EVENT, at: Date.now() }); }
    catch { /* channel closed — safe to ignore */ }
  }
}

/**
 * Subscribe to menu-directory changes. Returns an unsubscribe function.
 * The callback runs on both same-tab and cross-tab events; it should
 * be idempotent (e.g. re-fetch `/menu-visibility` and update state).
 */
export function subscribeMenuVisibility(callback) {
  const wrapped = () => {
    clearSessionCaches();
    try { callback(); } catch { /* consumer error must not break the bus */ }
  };
  try { window.addEventListener(WINDOW_EVENT, wrapped); } catch { /* noop */ }
  const ch = getChannel();
  const bcHandler = (ev) => {
    if (ev?.data?.type === WINDOW_EVENT) wrapped();
  };
  if (ch) ch.addEventListener("message", bcHandler);
  return () => {
    try { window.removeEventListener(WINDOW_EVENT, wrapped); } catch { /* noop */ }
    if (ch) {
      try { ch.removeEventListener("message", bcHandler); } catch { /* noop */ }
    }
  };
}
